/**
 * /app/pos/movimientos — POS: movimientos manuales de caja.
 * Ingresos y egresos manuales dentro de la sesión activa.
 *
 * SCRUM-90: un egreso puede vincularse opcionalmente a una contrapartida real
 * (orden de compra, recepción o proveedor de Abastecimiento) o, si no hay
 * contrapartida, clasificarse con un motivo. Nunca se pide un ID técnico —
 * se busca por nombre/número sobre la lista ya cargada (mismo criterio que
 * el resto del dominio, sin autocomplete server-side).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { PosMovimientosTour } from "@/components/ProductTour";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Clock, TrendingUp, TrendingDown, Plus, Link2 } from "lucide-react";
import { CrudEmptyState, CrudShellHeader, DataTable, Loading, Panel } from "@/components/ds";
import type { DataTableColumn } from "@/components/ds";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";

export const Route = createFileRoute("/app/pos/movimientos")({
  component: PosMovimientosPage,
  validateSearch: (s: Record<string, unknown>) => ({
    linkKind: s.linkKind === "purchase_order" || s.linkKind === "receipt" || s.linkKind === "supplier" ? s.linkKind : undefined,
    linkId: typeof s.linkId === "string" && s.linkId ? s.linkId : undefined,
    linkAmount: typeof s.linkAmount === "number" ? s.linkAmount : undefined,
  }),
  head: () => ({ meta: [{ title: "Movimientos de caja — POS" }] }),
});

type ReferenceType = "purchase_order" | "receipt" | "supplier";
type ExpenseReason =
  | "proveedor_mercaderia"
  | "proveedor_servicios"
  | "servicio_publico"
  | "mantenimiento"
  | "prestacion_profesional"
  | "gasto_operativo"
  | "otro";

interface CashMovement {
  id: string;
  type: "income" | "expense" | "sale" | "return";
  amount: number;
  description: string | null;
  createdAt: string;
  referenceType: ReferenceType | null;
  referenceId: string | null;
  expenseReason: ExpenseReason | null;
}

interface Counterparty {
  kind: ReferenceType;
  id: string;
  label: string;
  subtitle: string;
  amount: number | null;
  hasLinkedExpense: boolean;
}

const TYPE_LABELS = {
  income: { label: "Ingreso", color: "text-green-400", icon: TrendingUp },
  expense: { label: "Egreso", color: "text-red-400", icon: TrendingDown },
  sale: { label: "Venta", color: "text-blue-400", icon: TrendingUp },
  return: { label: "Devolución", color: "text-amber-400", icon: TrendingDown },
};

const REFERENCE_KIND_LABEL: Record<ReferenceType, string> = {
  purchase_order: "Orden de compra",
  receipt: "Recepción",
  supplier: "Proveedor",
};

const EXPENSE_REASON_OPTIONS: Array<{ value: ExpenseReason; label: string }> = [
  { value: "proveedor_mercaderia", label: "Proveedor de mercadería" },
  { value: "proveedor_servicios", label: "Proveedor de servicios" },
  { value: "servicio_publico", label: "Servicio público (luz, agua, gas, internet)" },
  { value: "mantenimiento", label: "Mantenimiento" },
  { value: "prestacion_profesional", label: "Prestación profesional" },
  { value: "gasto_operativo", label: "Gasto operativo" },
  { value: "otro", label: "Otro" },
];

const EXPENSE_REASON_LABEL: Record<ExpenseReason, string> = Object.fromEntries(
  EXPENSE_REASON_OPTIONS.map((o) => [o.value, o.label]),
) as Record<ExpenseReason, string>;

const pesos = (n: number) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);

type CounterpartyMode = "none" | "link" | "manual";

function PosMovimientosPage() {
  const tid = useWorkspaceTenantId();
  const { linkKind, linkId, linkAmount } = Route.useSearch();
  const [movements, setMovements] = useState<CashMovement[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: "income" as "income" | "expense", amount: "", description: "" });

  const [counterpartyMode, setCounterpartyMode] = useState<CounterpartyMode>("none");
  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  const [counterpartyQuery, setCounterpartyQuery] = useState("");
  const [selectedCounterparty, setSelectedCounterparty] = useState<Counterparty | null>(null);
  const [expenseReason, setExpenseReason] = useState<ExpenseReason | "">("");

  const [duplicateConfirm, setDuplicateConfirm] = useState<{
    message: string;
    existing: { id: string; amount: number; createdAt: string } | null;
  } | null>(null);

  const counterpartyByKey = useMemo(() => {
    const map = new Map<string, Counterparty>();
    for (const c of counterparties) map.set(`${c.kind}:${c.id}`, c);
    return map;
  }, [counterparties]);

  const load = async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const r = (await apiGetJson(`/api/pos/session`, tid)) as { session?: { id: string } };
      if (!r.session) {
        setLoading(false);
        return;
      }
      setSessionId(r.session.id);
      const [mr, cr] = await Promise.all([
        apiGetJson(`/api/pos/movements?sessionId=${r.session.id}`, tid) as Promise<{ movements?: CashMovement[] }>,
        apiGetJson(`/api/pos/movements/counterparties`, tid).catch(() => ({ results: [] })) as Promise<{
          results?: Counterparty[];
        }>,
      ]);
      setMovements(mr.movements ?? []);
      setCounterparties(cr.results ?? []);
    } catch {
      toast.error("Error al cargar movimientos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [tid]);

  // SCRUM-90: llegada desde "Generar egreso" en Abastecimiento — precompleta
  // el formulario con la contrapartida ya elegida, sin pedirle al usuario
  // que la busque de nuevo. Solo una vez: no debe reimponerse si el usuario
  // ya editó o canceló el formulario manualmente.
  const appliedLinkRef = useRef(false);
  useEffect(() => {
    if (appliedLinkRef.current) return;
    if (!linkKind || !linkId || counterparties.length === 0) return;
    const match = counterparties.find((c) => c.kind === linkKind && c.id === linkId);
    if (!match) return;
    appliedLinkRef.current = true;
    setForm((f) => ({ ...f, type: "expense", amount: linkAmount != null ? String(linkAmount) : f.amount }));
    setCounterpartyMode("link");
    setSelectedCounterparty(match);
    setShowForm(true);
  }, [linkKind, linkId, linkAmount, counterparties]);

  const resetExpenseLink = () => {
    setCounterpartyMode("none");
    setCounterpartyQuery("");
    setSelectedCounterparty(null);
    setExpenseReason("");
  };

  const submit = async (confirmDuplicate: boolean) => {
    if (!tid || !sessionId) return;
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) {
      toast.error("Monto inválido");
      return;
    }
    if (form.type === "expense" && counterpartyMode === "link" && !selectedCounterparty) {
      toast.error("Elegí una contrapartida o cambiá a egreso sin vínculo");
      return;
    }
    setSaving(true);
    try {
      await apiSendJson(
        `/api/pos/movements`,
        "POST",
        {
          sessionId,
          type: form.type,
          amount,
          description: form.description.trim() || null,
          referenceType: form.type === "expense" && counterpartyMode === "link" ? selectedCounterparty?.kind ?? null : null,
          referenceId: form.type === "expense" && counterpartyMode === "link" ? selectedCounterparty?.id ?? null : null,
          expenseReason: form.type === "expense" && counterpartyMode === "manual" && expenseReason ? expenseReason : null,
          confirmDuplicate,
        },
        tid,
      );
      toast.success("Movimiento registrado");
      setForm({ type: "income", amount: "", description: "" });
      resetExpenseLink();
      setShowForm(false);
      setDuplicateConfirm(null);
      load();
    } catch (e) {
      const err = e as Error & { code?: string; body?: { error?: string; existingMovement?: { id: string; amount: number; createdAt: string } } };
      if (err.code === "duplicate_expense" && err.body?.existingMovement) {
        setDuplicateConfirm({ message: err.body.error ?? err.message, existing: err.body.existingMovement });
      } else {
        toast.error(err.message || "Error al registrar");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => submit(false);

  if (loading) {
    return (
      <div>
        <CrudShellHeader title="Movimientos de caja" description="Ingresos y egresos manuales de la sesión activa" />
        <Loading variant="list" rows={4} />
      </div>
    );
  }

  const filteredCounterparties =
    counterpartyQuery.trim().length === 0
      ? counterparties.slice(0, 30)
      : counterparties
          .filter((c) => `${c.label} ${c.subtitle}`.toLowerCase().includes(counterpartyQuery.trim().toLowerCase()))
          .slice(0, 30);

  const columns: DataTableColumn<CashMovement>[] = [
    {
      key: "tipo",
      header: "Tipo",
      render: (m) => {
        const cfg = TYPE_LABELS[m.type];
        const Icon = cfg.icon;
        return (
          <div className={`flex items-center gap-1.5 text-xs ${cfg.color}`}>
            <Icon className="h-3.5 w-3.5" /> {cfg.label}
          </div>
        );
      },
    },
    {
      key: "monto",
      header: "Monto",
      className: "text-right",
      render: (m) => {
        const cfg = TYPE_LABELS[m.type];
        const sign = m.type === "expense" || m.type === "return" ? -1 : 1;
        return (
          <span className={`tabular-nums font-semibold ${cfg.color}`}>
            {sign > 0 ? "+" : "−"}
            {pesos(m.amount)}
          </span>
        );
      },
    },
    {
      key: "descripcion",
      header: "Descripción",
      render: (m) => {
        const linked = m.referenceType && m.referenceId ? counterpartyByKey.get(`${m.referenceType}:${m.referenceId}`) : null;
        return (
          <div>
            <span className="text-muted-foreground">{m.description ?? "—"}</span>
            {linked && (
              <div className="mt-0.5 flex items-center gap-1 text-xs text-primary">
                <Link2 className="h-3 w-3" /> Relacionado con: {linked.label}
              </div>
            )}
            {!linked && m.referenceType && (
              <div className="mt-0.5 flex items-center gap-1 text-xs text-primary">
                <Link2 className="h-3 w-3" /> Relacionado con {REFERENCE_KIND_LABEL[m.referenceType]}
              </div>
            )}
            {m.expenseReason && (
              <Badge variant="outline" className="mt-1 text-[10px]">
                {EXPENSE_REASON_LABEL[m.expenseReason]}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: "hora",
      header: "Hora",
      render: (m) => (
        <span className="text-xs text-muted-foreground tabular-nums">
          {new Date(m.createdAt).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false })}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PosMovimientosTour />
      <CrudShellHeader
        title="Movimientos de caja"
        description="Ingresos y egresos manuales de la sesión activa"
        actions={sessionId ? (
          <Button size="sm" className="gap-1.5" onClick={() => setShowForm((v) => !v)}>
            <Plus className="h-4 w-4" /> Registrar movimiento
          </Button>
        ) : undefined}
      />
      <ClinicalPageShell offset="none">
      {!sessionId ? (
        <CrudEmptyState
          icon={<Clock className="h-8 w-8" />}
          title="No hay sesión de caja activa"
          description="Abrí una sesión desde POS para registrar movimientos."
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/app/pos">Ir a POS</Link>
            </Button>
          }
        />
      ) : (
        <>
          {showForm && (
            <Panel title="Nuevo movimiento manual">
              <div className="flex flex-wrap gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Tipo</label>
                  <select
                    value={form.type}
                    onChange={(e) => {
                      const type = e.target.value as "income" | "expense";
                      setForm((f) => ({ ...f, type }));
                      if (type === "income") resetExpenseLink();
                    }}
                    className="mt-1 block rounded-md border border-border/60 bg-card px-3 py-1.5 text-sm"
                  >
                    <option value="income">Ingreso</option>
                    <option value="expense">Egreso</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Monto</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                    placeholder="0.00"
                    className="mt-1 block w-32 rounded-md border border-border/60 bg-card px-3 py-1.5 text-sm text-right tabular-nums"
                  />
                </div>
                <div className="flex-1 min-w-40">
                  <label className="text-xs text-muted-foreground">Descripción (opcional)</label>
                  <input
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Ej: Pago a proveedor"
                    className="mt-1 block w-full rounded-md border border-border/60 bg-card px-3 py-1.5 text-sm"
                  />
                </div>
              </div>

              {form.type === "expense" && (
                <div className="mt-4 rounded-lg border border-border/60 p-3">
                  <label className="text-xs text-muted-foreground">¿A qué corresponde este egreso?</label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <Button
                      size="sm"
                      variant={counterpartyMode === "none" ? "secondary" : "outline"}
                      onClick={() => { setCounterpartyMode("none"); setSelectedCounterparty(null); setExpenseReason(""); }}
                    >
                      Sin vínculo
                    </Button>
                    <Button
                      size="sm"
                      variant={counterpartyMode === "link" ? "secondary" : "outline"}
                      onClick={() => { setCounterpartyMode("link"); setExpenseReason(""); }}
                    >
                      Vincular a abastecimiento/proveedor existente
                    </Button>
                    <Button
                      size="sm"
                      variant={counterpartyMode === "manual" ? "secondary" : "outline"}
                      onClick={() => { setCounterpartyMode("manual"); setSelectedCounterparty(null); }}
                    >
                      Egreso manual (indicar motivo)
                    </Button>
                  </div>

                  {counterpartyMode === "link" && (
                    <div className="mt-3">
                      <input
                        value={counterpartyQuery}
                        onChange={(e) => setCounterpartyQuery(e.target.value)}
                        placeholder="Buscar por número de OC, recepción o nombre de proveedor…"
                        className="block w-full rounded-md border border-border/60 bg-card px-3 py-1.5 text-sm"
                      />
                      <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-border/40">
                        {filteredCounterparties.length === 0 ? (
                          <p className="p-3 text-xs text-muted-foreground">No se encontraron resultados.</p>
                        ) : (
                          filteredCounterparties.map((c) => (
                            <button
                              key={`${c.kind}:${c.id}`}
                              type="button"
                              onClick={() => setSelectedCounterparty(c)}
                              className={`flex w-full items-center justify-between gap-2 border-b border-border/20 px-3 py-2 text-left text-sm last:border-0 hover:bg-muted/40 ${
                                selectedCounterparty?.kind === c.kind && selectedCounterparty?.id === c.id
                                  ? "bg-primary/10 border-l-2 border-l-primary"
                                  : ""
                              }`}
                            >
                              <span>
                                <span className="font-medium">{c.label}</span>
                                <span className="ml-1.5 text-xs text-muted-foreground">{c.subtitle}</span>
                              </span>
                              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                {c.hasLinkedExpense && <Badge variant="outline" className="text-[10px]">Ya tiene egreso</Badge>}
                                {REFERENCE_KIND_LABEL[c.kind]}
                              </span>
                            </button>
                          ))
                        )}
                      </div>
                      {selectedCounterparty && (
                        <p className="mt-1.5 text-xs text-primary">
                          Relacionado con: {selectedCounterparty.label} ({REFERENCE_KIND_LABEL[selectedCounterparty.kind]})
                        </p>
                      )}
                    </div>
                  )}

                  {counterpartyMode === "manual" && (
                    <div className="mt-3">
                      <label className="text-xs text-muted-foreground">Motivo del egreso</label>
                      <select
                        value={expenseReason}
                        onChange={(e) => setExpenseReason(e.target.value as ExpenseReason)}
                        className="mt-1 block w-full rounded-md border border-border/60 bg-card px-3 py-1.5 text-sm"
                      >
                        <option value="">Elegí un motivo…</option>
                        {EXPENSE_REASON_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={handleAdd} disabled={saving}>
                  {saving ? "Guardando..." : "Registrar"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); resetExpenseLink(); }}>
                  Cancelar
                </Button>
              </div>
            </Panel>
          )}

          <DataTable
            columns={columns}
            rows={movements}
            getRowKey={(m) => m.id}
            emptyState={<CrudEmptyState title="Sin movimientos en esta sesión" />}
          />
        </>
      )}
    </ClinicalPageShell>

      <AlertDialog open={!!duplicateConfirm} onOpenChange={(open) => !open && setDuplicateConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ya existe un egreso para esta contrapartida</AlertDialogTitle>
            <AlertDialogDescription>
              {duplicateConfirm?.message}
              {duplicateConfirm?.existing && (
                <>
                  {" "}Egreso previo por {pesos(duplicateConfirm.existing.amount)} registrado el{" "}
                  {new Date(duplicateConfirm.existing.createdAt).toLocaleDateString("es-AR")}. ¿Registrar igual este nuevo egreso?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => submit(true)}>Registrar igual</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
